import { memo, useMemo, type ReactNode } from "react";
import { inlineText, parseBlocks, parseInline, type Block, type Inline } from "./markdown";

const inline = (nodes: Inline[]): ReactNode[] =>
  nodes.map((n, i) => {
    switch (n.t) {
      case "text":
        return n.v;
      case "code":
        return <code key={i}>{n.v}</code>;
      case "strong":
        return <strong key={i}>{inline(n.c)}</strong>;
      case "em":
        return <em key={i}>{inline(n.c)}</em>;
      case "del":
        return <del key={i}>{inline(n.c)}</del>;
      case "br":
        return <br key={i} />;
      case "link":
        return (
          <a key={i} href={n.href} target="_blank" rel="noopener noreferrer nofollow">
            {inline(n.c)}
          </a>
        );
    }
  });

const block = (b: Block, i: number): ReactNode => {
  switch (b.t) {
    case "p":
      return <p key={i}>{inline(b.c)}</p>;
    case "h": {
      const H = `h${Math.min(6, b.level)}` as "h1";
      return <H key={i}>{inline(b.c)}</H>;
    }
    case "code":
      return (
        <pre key={i} className="md-code" data-lang={b.lang || undefined} tabIndex={0}>
          <code>{b.v}</code>
        </pre>
      );
    case "quote":
      return <blockquote key={i}>{b.c.map(block)}</blockquote>;
    case "hr":
      return <hr key={i} />;
    case "list": {
      const L = b.ordered ? "ol" : "ul";
      return (
        <L key={i} {...(b.ordered && b.start !== 1 ? { start: b.start } : {})} className={b.items.some((x) => x.task) ? "md-tasks" : undefined}>
          {b.items.map((it, j) => (
            <li key={j} className={it.task ? "md-task" : undefined}>
              {it.task && <input type="checkbox" checked={!!it.checked} readOnly disabled aria-label={it.checked ? "Done" : "Not done"} />}
              {it.c.map(block)}
            </li>
          ))}
        </L>
      );
    }
    case "table":
      return (
        <div key={i} className="md-table" role="region" tabIndex={0} aria-label="Table">
          <table>
            <thead>
              <tr>
                {b.head.map((c, j) => (
                  <th key={j} style={b.align[j] ? { textAlign: b.align[j]! } : undefined}>
                    {inline(c)}
                  </th>
                ))}
              </tr>
            </thead>
            {b.rows.length > 0 && (
              <tbody>
                {b.rows.map((r, k) => (
                  <tr key={k}>
                    {r.map((c, j) => (
                      <td key={j} style={b.align[j] ? { textAlign: b.align[j]! } : undefined}>
                        {inline(c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      );
  }
};

/** What an agent wrote, drawn as formatted text (headings, bold, code, lists, tables). Built from a tree, never from HTML. */
export const Markdown = memo(function Markdown({ text }: { text: string }) {
  const blocks = useMemo(() => parseBlocks(text), [text]);
  return <div className="md">{blocks.map(block)}</div>;
});

/** One line of agent text with its marks drawn (a question, a label): no blocks. */
export function InlineMd({ text }: { text: string }) {
  return <>{inline(parseInline(text))}</>;
}

/** The same text without the marks, for a one-line preview or a notification. */
export const plainMd = (text: string): string => inlineText(parseInline(text));
